import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useState } from "react";

export default function Contact() {
	const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
	const [submitted, setSubmitted] = useState(false);

	const contactInfo = [
		{
			icon: <Phone size={20} />,
			label: "Phone",
			value: "+20 100 000 0000",
			sub: "",
			href: "tel:+201000000000",
		},
		{
			icon: <Mail size={20} />,
			label: "Email",
			value: "hello@dawi.app",
			href: "mailto:hello@dawi.app",
		},
		{
			icon: <MapPin size={20} />,
			label: "Location",
			value: "Egypt",
			href: "#",
		},

	];



	function handleChange(
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
	) {
		setForm({ ...form, [e.target.name]: e.target.value });
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitted(true);
	}

	return (
		<section id="contact" className="bg-white w-full">
			<div className="max-w-6xl mx-auto px-6 py-20 flex flex-col gap-16">

				{/* Header */}
				<div className="flex flex-col items-center text-center gap-3">
					<span className="text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide" style={{ backgroundColor: "#b2ebf2", color: "#2C8C87" }}>
						Contact Us
					</span>
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
						We'd love to <span style={{ color: "#2C8C87" }}>hear from you</span>
					</h2>
					<p className="text-gray-500 text-base max-w-md leading-relaxed">
						Have a question or want to partner with us? Reach out and our team
						will get back to you within 24 hours.
					</p>
				</div>

				{/* Main Content */}
				<div className="flex flex-col lg:flex-row gap-12">

					{/* Left Column */}
					<div className="flex flex-col justify-center gap-10 lg:w-96 flex-shrink-0">

						{/* Contact Cards */}
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
							{contactInfo.map((item) => (
								<a
									key={item.label}
									href={item.href}
									className="flex items-start gap-4 group p-4 rounded-2xl border border-gray-100 hover:border-[#76C7C0] hover:shadow-md transition-all duration-200"
								>
									<div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#76C7C0", color: "#1a5c58" }}>
										{item.icon}
									</div>
									<div className="flex flex-col">
										<span className="text-xs text-gray-400 mb-0.5">{item.label}</span>
										<span className="text-sm font-semibold text-gray-800 group-hover:text-[#2C8C87] transition-colors duration-200">
											{item.value}
										</span>
										<span className="text-xs text-gray-400 mt-0.5">{item.sub}</span>
									</div>
								</a>
							))}
						</div>

						{/* Quick FAQs removed */}
					</div>

					{/* Right Column — Form */}
					<div className="flex-1">
						<form
							onSubmit={handleSubmit}
							className="flex flex-col gap-4 bg-gray-50 rounded-3xl p-8 border border-gray-100"
						>
							{submitted ? (
								<div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
									<div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "#b2ebf2" }}>
										<svg width="28" height="28" viewBox="0 0 24 24" fill="none">
											<path d="M5 12l4 4L19 7" stroke="#2C8C87" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<h3 className="text-xl font-bold text-gray-900">Message sent!</h3>
									<p className="text-sm text-gray-500 max-w-xs">
										Thanks for reaching out. Our team will get back to you within 24 hours.
									</p>
								</div>
							) : (
								<>
									<h3 className="text-lg font-bold text-gray-900 mb-1">Send us a message</h3>

									<div className="flex flex-col sm:flex-row gap-4">
										<input
											type="text"
											name="name"
											value={form.name}
											onChange={handleChange}
											placeholder="Your name"
											required
											className="flex-1 border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-[#76C7C0] transition-colors duration-200"
										/>
										<input
											type="email"
											name="email"
											value={form.email}
											onChange={handleChange}
											placeholder="Your email"
											required
											className="flex-1 border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-[#76C7C0] transition-colors duration-200"
										/>
									</div>

									<div className="flex flex-col sm:flex-row gap-4">
										<input
											type="tel"
											name="phone"
											value={form.phone}
											onChange={handleChange}
											placeholder="Phone number (optional)"
											className="flex-1 border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-[#76C7C0] transition-colors duration-200"
										/>
										<select
											name="subject"
											value={form.subject}
											onChange={handleChange}
											required
											className="flex-1 border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#76C7C0] transition-colors duration-200"
										>
											<option value="" disabled>Select subject</option>
											<option value="general">General Inquiry</option>
											<option value="partnership">Partnership</option>
											<option value="support">Support</option>
											<option value="feedback">Feedback</option>
										</select>
									</div>

									<textarea
										name="message"
										value={form.message}
										onChange={handleChange}
										placeholder="Your message..."
										required
										rows={5}
										className="border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:border-[#76C7C0] transition-colors duration-200 resize-none"
									/>

									<div className="flex items-center justify-between flex-wrap gap-4 mt-1">
										<p className="text-xs text-gray-400">We'll never share your info with anyone.</p>
										<button
											type="submit"
											className="flex items-center justify-center gap-2 text-white font-semibold px-8 py-3 rounded-full shadow-md transition-colors duration-200"
											style={{ backgroundColor: "#76C7C0" }}
											onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#5ab5ae")}
											onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#76C7C0")}
										>
											<Send size={16} />
											Send Message
										</button>
									</div>
								</>
							)}
						</form>
					</div>
				</div>
			</div>
		</section>
	);
}